import { useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  MessageSquareWarning,
  Plus,
  Truck,
} from 'lucide-react';

const P = {
  white: '#FFFFFF',
  grid: '#E2E8F0',
  blue: '#3B82F6',
  btnBlue: '#BFDBFE',
  btnBlueBorder: '#93C5FD',
};

function ActionModal({ title, message, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      style={{ backgroundColor: 'rgba(241, 245, 249, 0.92)', colorScheme: 'light' }}
    >
      <div
        className="w-full max-w-md rounded-2xl border p-6 shadow-xl"
        style={{ backgroundColor: P.white, borderColor: P.grid, borderWidth: 1, borderStyle: 'solid' }}
      >
        <h4 className="text-lg font-bold text-black">{title}</h4>
        <p className="mt-2 text-sm text-black/70">{message}</p>
        <p
          className="mt-4 rounded-lg border px-3 py-2 text-xs font-semibold text-black"
          style={{ backgroundColor: P.btnBlue, borderColor: P.btnBlueBorder }}
        >
          Self-service request submitted — OGTS dispatch will respond within 2 business hours.
        </p>
        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full rounded-xl border py-2.5 text-sm font-bold text-black transition hover:opacity-90"
          style={{
            backgroundColor: P.btnBlue,
            borderColor: P.btnBlueBorder,
            borderWidth: 1,
            borderStyle: 'solid',
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}

const actionBtn =
  'flex flex-col items-center gap-2 rounded-xl border px-4 py-5 text-center text-sm font-bold text-black transition hover:opacity-90';

export default function RequestPanel() {
  const [modal, setModal] = useState(null);
  const [approved, setApproved] = useState(false);

  const actions = [
    {
      id: 'transport',
      label: 'Request New Transport / Extra Trucks',
      icon: Plus,
      style: { backgroundColor: P.btnBlue, borderColor: P.btnBlueBorder },
      modalTitle: 'New Transport Request',
      modalMessage:
        'Submit additional tanker requirements for your upcoming consignments. Specify product type, volume, and preferred pickup window.',
    },
    {
      id: 'complaint',
      label: 'Raise Operations Complaint',
      icon: MessageSquareWarning,
      style: { backgroundColor: '#FEE2E2', borderColor: '#FCA5A5' },
      modalTitle: 'Operations Complaint',
      modalMessage:
        'Report delivery delays, product quality concerns, or driver conduct issues. Reference your consignment ID for faster resolution.',
    },
    {
      id: 'approve',
      label: 'Approve Pending Delivery',
      icon: CheckCircle2,
      style: { backgroundColor: '#DCFCE7', borderColor: '#86EFAC' },
      modalTitle: 'Delivery Approval',
      modalMessage:
        'Review and approve pending delivery for CON-2404 — Bitumen to Dubai South Infrastructure.',
    },
  ];

  return (
    <>
      <section
        className="rounded-2xl border p-5 shadow-sm"
        style={{ backgroundColor: P.white, borderColor: P.grid, borderWidth: 1, borderStyle: 'solid' }}
      >
        <div className="mb-4 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" style={{ color: P.blue }} strokeWidth={1.75} />
          <div>
            <h3 className="text-base font-bold text-black">Interactive Request Panel</h3>
            <p className="text-xs text-black/70">Self-service actions · No call required</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {actions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                type="button"
                onClick={() => {
                  if (action.id === 'approve') setApproved(true);
                  setModal(action);
                }}
                className={actionBtn}
                style={{ ...action.style, borderWidth: 1, borderStyle: 'solid' }}
              >
                <Icon className="h-6 w-6" strokeWidth={1.75} />
                {action.label}
              </button>
            );
          })}
        </div>

        {approved && (
          <div
            className="mt-4 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold text-black"
            style={{ backgroundColor: '#DCFCE7', borderColor: '#86EFAC' }}
          >
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            CON-2404 delivery approved — dispatch notified.
          </div>
        )}

        <p className="mt-4 flex items-center gap-1.5 text-[11px] text-black/70">
          <Truck className="h-3.5 w-3.5" />
          Enterprise SLA: 2-hour response · 24/7 portal access
        </p>
      </section>

      {modal && (
        <ActionModal
          title={modal.modalTitle}
          message={modal.modalMessage}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}
